import { Column, ColumnSortEvent } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import { useGetCalculationsByFootPrintId } from '../../../../../../../../../../../../hooks/api/footprints';
import { useAuth } from '../../../../../../../../../../../../hooks/auth/useAuth';
import { Locale } from '../../../../../../../../../../../../i18n';
import EmissionValueWithSuffix from '../../../../../../../../../../../../shared/components/emission-value-with-suffix/EmissionValueWithSuffix';
import { parseDateWithTime } from '../../../../../../../../../../../../shared/helpers/parse-date';
import {
  CalculationStatus,
  EmissionSymbol,
  ICalculationDetailsTableRow,
} from '../../../../../../../../../../../../shared/interfaces/IFootprint';
import CopyAsNewButton from '../copy-as-new-button/CopyAsNewButton';
import EditButton from '../edit-button/EditButton';
import PublishButton from '../publish-button/PublishButton';
import style from './CalculationTable.module.css';

export type CalculationTableProps = {
  productId: string;
  footprintId: string;
};

const CalculationTable = ({ productId, footprintId }: CalculationTableProps): JSX.Element => {
  // Getting the calculation details by footprint Id
  const {
    data: calculationsData,
    isLoading,
    isError,
  } = useGetCalculationsByFootPrintId(Number(footprintId), {
    enabled: !!footprintId,
    retry: 0,
  });
  const navigate = useNavigate();
  const { user } = useAuth();

  // If footprint details not found then navigate to 404 page
  if (isError) {
    return <Navigate to='/404' />;
  }

  const {
    i18n: { language },
    t,
  } = useTranslation();

  // Check if user has basic license
  const isBasicLicense = user?.company?.license === 'basic';

  const sortPcfValueColumn = (event: ColumnSortEvent) => {
    const data = [...event.data] as ICalculationDetailsTableRow[];
    const order = event.order || 1;

    data.sort((data1, data2) => {
      const pcf1 = data1?.pcfExcludingBiogenic || 0;
      const pcf2 = data2?.pcfExcludingBiogenic || 0;

      return (pcf1 - pcf2) * order;
    });

    return data;
  };

  const nameTemplate = (rowData: ICalculationDetailsTableRow): JSX.Element => {
    const href = `/products/${productId}/pcf/footprint/${footprintId}/calculations/${rowData.id}`;

    const name = rowData?.name ?? t('global.notSpecified');

    return (
      <div className='flex flex-column justify-content-center'>
        <div
          onClick={() => {
            return navigate(href);
          }}
          className={'font-normal max-w-fit hover:underline cursor-pointer'}
        >
          {name}
        </div>
      </div>
    );
  };

  const pcfColumnHeaderTemplate = (): JSX.Element => {
    return (
      <div className='flex flex-column'>
        <span>{t('calculationsPage.calculationTableColumns.pcfValue')}</span>
        <span className='text-sm font-light'>
          {t('calculationsPage.calculationTableColumns.exclBiogenicEmissions')}
        </span>
      </div>
    );
  };

  const pcfTemplate = (rowData: ICalculationDetailsTableRow): JSX.Element | string => {
    // For basic license users, never render the actual PCF value in the DOM
    if (isBasicLicense) {
      return (
        <div className='flex flex-row align-items-center gap-2'>
          <div className='blurred flex flex-row align-items-center gap-1'>
            <div className='flex p-2 bg-gray-100 w-8 border-round-2xl' />
            <div className='flex p-2 bg-gray-50 w-6 border-round-2xl' />
          </div>
        </div>
      );
    }

    const emissionValue = rowData?.pcfExcludingBiogenic;
    if (!emissionValue) {
      return t('global.notSpecified');
    }

    return (
      <EmissionValueWithSuffix
        emissionValue={emissionValue}
        emissionSymbol={EmissionSymbol.KG_CO2E}
        declaredUnit={rowData.declaredUnit}
        abbreviateUnit
        unitaryProductAmount={rowData.unitaryProductAmount}
      />
    );
  };

  const buttonTemplate = (rowData: ICalculationDetailsTableRow): JSX.Element | null => {
    if (rowData.status === CalculationStatus.DRAFT) {
      return (
        <div className='flex flex-row'>
          <EditButton
            productId={productId}
            footprintId={footprintId}
            calculationId={rowData.id.toString()}
          />
        </div>
      );
    } else if (rowData.status === CalculationStatus.COMPLETE) {
      return (
        <div className='flex flex-row gap-4'>
          <CopyAsNewButton
            productId={productId}
            footprintId={footprintId}
            calculationId={rowData.id.toString()}
          />
          {rowData?.name && (
            <PublishButton
              productId={Number(productId)}
              footprintId={Number(footprintId)}
              calculationId={rowData.id}
            />
          )}
        </div>
      );
    }

    return null;
  };

  const dateCreatedTemplate = (rowData: ICalculationDetailsTableRow): React.ReactNode => {
    return parseDateWithTime(rowData.dateCreated ?? '', language as Locale);
  };

  return (
    <div className={style['calculation-table']}>
      <DataTable
        value={calculationsData}
        loading={isLoading}
        selectionMode='single'
        sortField='dateCreated'
        sortOrder={-1}
        scrollable
        scrollHeight='600px'
        emptyMessage={t('global.emptyTableMessage')}
      >
        <Column
          header={t('calculationsPage.calculationTableColumns.name')}
          body={nameTemplate}
          className='w-3'
        />
        <Column
          header={t('calculationsPage.calculationTableColumns.dateCreated')}
          body={dateCreatedTemplate}
          sortable
          sortField='dateCreated'
          className='w-3'
        />

        <Column
          header={pcfColumnHeaderTemplate}
          body={pcfTemplate}
          sortable
          sortField='pcfExcludingBiogenic'
          sortFunction={sortPcfValueColumn}
          className='w-3'
        />
        <Column body={buttonTemplate} className='w-3' />
      </DataTable>
    </div>
  );
};

export default CalculationTable;
